import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox'
import { Dropdown } from 'primereact/dropdown'
import { Tree } from 'primereact/tree'
import { classNames } from 'primereact/utils'
import { FC, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import { useGetCupProjectRunCoefficientData } from '../../api/report-API'
import { useGetUsers } from '../../api/user-API'
import { protoService } from '../../proto/ProtoService'
import { currentTeamState, currentUserState } from '../../utils/state-atoms'
import { CheckboxStyle } from '../../utils/styling-constants'

const PROJECT_SELECTION_STORAGE_KEY = 'projectRunSelection4Submission'
const KEY_DELIMITER = '::'

const projectRunTreeStyle = { minHeight: 'calc(100% - 30px)', maxHeight: 'calc(100% - 30px)' }

type ProjectRunSelector4SubmissionProps = {
  showFilter: boolean
  storageScope?: string
  onDataLoading: () => void
  onDoneLoading: (data: any) => void
  initialProjectRunSelection?: Record<string, any> | null
  onProjectRunSelectionChanged: (projects: string[], runs: string[]) => void
  onReset?: () => void
}

type TreeNode = {
  key: string
  label: string
  children?: TreeNode[]
}

const togglerTemplate = (node, options) => {
  if (!node) {
    return
  }

  const expanded = options.expanded
  const iconClassName = classNames('p-tree-toggler-icon pi pi-fw text-xs pr-1', {
    'pi-chevron-right': !expanded && node.children,
    'pi-chevron-down': expanded && node.children
  })

  return (
    <button type="button" className="p-tree-toggler p-link" tabIndex={-1} onClick={options.onClick}>
      <span className={iconClassName} aria-hidden="true"></span>
    </button>
  )
}

const ProjectRunSelector4Submission: FC<ProjectRunSelector4SubmissionProps> = ({
  initialProjectRunSelection = null,
  onProjectRunSelectionChanged,
  onReset,
  onDataLoading,
  onDoneLoading,
  showFilter
}: ProjectRunSelector4SubmissionProps) => {
  const [projectRunNodes, setProjectRunNodes] = useState<TreeNode[]>([])
  const [selectedProjectRunKeys, setSelectedProjectRunKeys] = useState(null)

  const [byMeOnly, setByMeOnly] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [userOptions, setUserOptions] = useState([])
  const [{ loading: projectRunLoading }, getProjectRunData] = useGetCupProjectRunCoefficientData()
  const refRawProjectRunData = useRef<any[]>([])
  const currentTeam = useRecoilValue(currentTeamState)
  const currentUser = useRecoilValue(currentUserState)
  const refFirstLoad = useRef(true)
  const refInitialProjectRunSelection = useRef(initialProjectRunSelection)
  const [{ loading: loadingUsers }, getUsers] = useGetUsers()

  const getStorageKey = (baseKey: string) => {
    const teamScope = currentTeam || 'unknown-team'
    return `${baseKey}:${teamScope}`
  }

  const readSelectionFromStorage = (storageKey: string) => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (!raw) {
        return {}
      }
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch (error) {
      console.error(`Failed to parse selection for key ${storageKey}:`, error)
      return {}
    }
  }

  const filterSelectionByAllowedKeys = (
    selection: Record<string, any>,
    allowedKeys: Set<string>
  ) => {
    return Object.entries(selection).reduce(
      (acc, [key, value]) => {
        if (allowedKeys.has(key) && (value?.checked || value?.partialChecked)) {
          acc[key] = { checked: value?.checked, partialChecked: value?.partialChecked }
        }
        return acc
      },
      {} as Record<string, { checked: boolean; partialChecked?: boolean }>
    )
  }

  const handleShowMeOnlyChange = (evt: CheckboxChangeEvent) => {
    setByMeOnly(evt.checked as boolean)
    setSelectedUserId(currentUser._id)
  }

  const handleProjectRunSelectionChange = e => {
    setSelectedProjectRunKeys(e.value)

    const allSelectedProjects: Set<string> = new Set()
    const allSelectedRuns: Set<string> = new Set()

    // find all selected projects and states associated with selected projects/runs
    Object.entries(e.value).forEach(([key, value]: [string, any]) => {
      if (value.checked) {
        if (key.includes(KEY_DELIMITER)) {
          // run node
          const parts = key.split(KEY_DELIMITER)
          const projectKey = parts[0]
          const runKey = parts[1]
          allSelectedProjects.add(projectKey)
          allSelectedRuns.add(runKey)
        } else {
          // project node
          allSelectedProjects.add(key)
        }
      }
    })

    onProjectRunSelectionChanged(Array.from(allSelectedProjects), Array.from(allSelectedRuns))

    localStorage.setItem(getStorageKey(PROJECT_SELECTION_STORAGE_KEY), JSON.stringify(e.value))
  }

  const buildSelectAllProjectRunSelection = () => {
    const nextSelection: Record<string, { checked: boolean }> = {}

    const visitNode = (node: TreeNode) => {
      nextSelection[node.key] = { checked: true }
      node.children?.forEach(visitNode)
    }

    projectRunNodes.forEach(visitNode)
    return nextSelection
  }

  const handleSelectAllProjectRuns = () => {
    const nextSelection = buildSelectAllProjectRunSelection()
    handleProjectRunSelectionChange({ value: nextSelection })
  }

  const handleDeselectAllProjectRuns = () => {
    handleProjectRunSelectionChange({ value: {} })
  }

  const dataArrayToTreeNodes = (dataArray: any[]) => {
    const projectMap: Record<string, any> = {}
    dataArray.forEach((row: any) => {
      const project = row['project']
      const run = row['run']
      if (!projectMap[project]) {
        projectMap[project] = {}
      }
      if (!projectMap[project][run]) {
        projectMap[project][run] = []
      }
    })

    const treeNodes = Object.keys(projectMap).map(project => ({
      key: project,
      label: project,
      children: Object.keys(projectMap[project]).map(run => {
        const runKey = `${project}${KEY_DELIMITER}${run}`
        return {
          key: runKey,
          label: run
        }
      })
    }))

    return treeNodes
  }

  const initData = async () => {
    try {
      onDataLoading && onDataLoading()
      const projectRunRes = (await getProjectRunData({} as any)) as any
      // onFilterLoadingChanged && onFilterLoadingChanged(true)

      refRawProjectRunData.current = projectRunRes
        ? projectRunRes.data.map(row => {
            return row
          })
        : []

      const projNodesData = dataArrayToTreeNodes(refRawProjectRunData.current)
      const allProjectRunKeys = new Set<string>()
      projNodesData.forEach(projectNode => {
        allProjectRunKeys.add(projectNode.key)
        projectNode.children?.forEach(runNode => {
          allProjectRunKeys.add(runNode.key)
        })
      })

      const initialProjectRunSelectionToApply = refInitialProjectRunSelection.current
      refInitialProjectRunSelection.current = null
      const persistedProjectRunSelection =
        initialProjectRunSelectionToApply ||
        readSelectionFromStorage(getStorageKey(PROJECT_SELECTION_STORAGE_KEY))
      const validPersistedProjectRunSelection = filterSelectionByAllowedKeys(
        persistedProjectRunSelection,
        allProjectRunKeys
      )
      let projectRunSelection: any = {}
      const runs: string[] = []
      const selectedProjects: string[] = []
      const usingPersistedSelection = Object.keys(validPersistedProjectRunSelection).length > 0
      if (usingPersistedSelection) {
        projectRunSelection = validPersistedProjectRunSelection
        // Derive projects and runs from the restored selection
        Object.entries(projectRunSelection).forEach(([key, value]: [string, any]) => {
          if (value?.checked) {
            if (key.includes(KEY_DELIMITER)) {
              runs.push(key)
              const projectKey = key.split(KEY_DELIMITER)[0]
              if (!selectedProjects.includes(projectKey)) {
                selectedProjects.push(projectKey)
              }
            } else {
              if (!selectedProjects.includes(key)) {
                selectedProjects.push(key)
              }
            }
          }
        })
      }

      setProjectRunNodes(projNodesData)
      handleProjectRunSelectionChange({ value: projectRunSelection })
      if (selectedProjects.length > 0) {
        // Let parent component know the selection and load data
        onProjectRunSelectionChanged && onProjectRunSelectionChanged(selectedProjects, runs)
      }

      onDoneLoading && onDoneLoading(refRawProjectRunData.current)
    } catch (error: any) {
      console.error('Error fetching project/run data:', {
        error,
        errorCode: error?.code,
        errorMessage: error?.message,
        errorResponse: error?.response,
        statusCode: error?.response?.status,
        statusText: error?.response?.statusText,
        responseData: error?.response?.data,
        currentTeam
      })
      // Show user-friendly error message
      if (error?.code === 'ECONNABORTED' || error?.code === 'ERR_NETWORK') {
        toast.error('Network timeout loading projects. Please check your connection and refresh.')
      } else if (error?.response?.status === 401 || error?.response?.status === 403) {
        toast.error('Authentication error. Please log out and log back in.')
      } else if (error?.response?.status === 500 || error?.response?.status === 503) {
        toast.error('Server error loading project list. Please try again or contact support.')
      } else {
        toast.error('Error loading project list. Please refresh the page or contact support.')
      }
      onDoneLoading && onDoneLoading(null)
    } finally {
      // onFilterLoadingChanged && onFilterLoadingChanged(false)
    }
  }

  useEffect(() => {
    getUsers()
      .then(engData => {
        if (engData.data) {
          const engs = protoService.decodeProtoResponse(engData.data, 'user')
          const userOptions = engs.map(user => {
            return {
              label: `${user.firstName} ${user.lastName}`,
              value: user._id
            }
          })
          setUserOptions(userOptions)
        }
      })
      .catch(err => {
        console.error(err)
        toast.error('Error loading engineers list.')
      })
    initData()
  }, [])

  useEffect(() => {
    if (refFirstLoad.current) {
      refFirstLoad.current = false
      return // Skip on first render
    }
    if (currentTeam) {
      // clear saved selection and local state
      setProjectRunNodes([])
      setSelectedProjectRunKeys(null)
      onReset && onReset()
      initData()
    }
  }, [currentTeam, selectedUserId])

  return (
    <div
      className="flex flex-col items-stretch w-full space-y-2 overflow-y-auto"
      style={{ height: 'calc(100vh - 280px)', maxHeight: 'calc(100vh - 280px)', width: '320px' }}>
      {/* filter box*/}
      <div
        className={
          showFilter ? 'flex flex-col items-stretch border rounded-lg p-4 space-y-2' : 'hidden'
        }>
        <div className="font-bold">Filters</div>
        <div className="flex items-center">
          <Checkbox
            inputId="byMeOnly"
            pt={CheckboxStyle}
            onChange={handleShowMeOnlyChange}
            checked={byMeOnly}></Checkbox>
          <label htmlFor="byMeOnly" className="ml-2 font-normal">
            Submitted by Me Only
          </label>
        </div>
        <div className="flex items-center space-x-2">
          <label className="font-normal">Filter by User:</label>
          <Dropdown
            filter
            showClear
            disabled={byMeOnly}
            loading={loadingUsers}
            value={selectedUserId}
            options={userOptions}
            onChange={e => {
              setSelectedUserId(e.value)
            }}
            placeholder="Select a user"
            pt={{
              root: {
                className: 'max-w-full basis-0 grow'
              },
              input: {
                className: '!p-2'
              },
              trigger: {
                className: '!w-8'
              },
              clearIcon: {
                className: '!right-8'
              }
            }}
          />
        </div>
      </div>
      <div
        className={`basis-0 grow shrink-0 flex flex-col items-stretch space-y-2 p-4 !border border-solid rounded-lg`}>
        <div className="flex items-center justify-between">
          <div className="font-semibold text-left text-gm-blue-dark dark:text-neutral-20">
            Project - Run
          </div>
          <div className="flex items-center space-x-4">
            <button
              type="button"
              disabled={projectRunLoading || projectRunNodes.length === 0}
              onClick={handleSelectAllProjectRuns}
              className="text-xs text-gm-blue disabled:text-neutral-400 hover:underline disabled:no-underline">
              Select All
            </button>
            <button
              type="button"
              disabled={projectRunLoading}
              onClick={handleDeselectAllProjectRuns}
              className="text-xs text-gm-blue disabled:text-neutral-400 hover:underline disabled:no-underline">
              Deselect All
            </button>
          </div>
        </div>
        <Tree
          value={projectRunNodes}
          disabled={projectRunLoading}
          emptyMessage={() => {
            return projectRunLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <i className="pi pi-spin pi-spinner text-xl text-gm-blue"></i>
                <div>Loading...</div>
              </div>
            ) : (
              'No projects/runs available.'
            )
          }}
          selectionKeys={selectedProjectRunKeys}
          onSelectionChange={handleProjectRunSelectionChange}
          selectionMode="checkbox"
          className="h-full border-0 checkbox-tree"
          togglerTemplate={togglerTemplate}
          style={projectRunTreeStyle}
          pt={{
            root: {
              className: '!p-0 basis-0 grow !bg-transparent'
            },
            content: {
              className: '!p-0'
            },
            node: {
              className: '!p-0'
            },
            label: {
              className: 'text-xs'
            },
            input: {
              className: 'w-4 h-4'
            },
            container: {
              className: 'overflow-y-auto h-full'
            }
          }}
        />
      </div>
    </div>
  )
}

export default ProjectRunSelector4Submission
